import { Spinner } from "@components/ui/spinner";
import {
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "@components/ui/alert-dialog";
import { Trans, useLingui } from "@lingui/react/macro";
import { generatePath, useHistory, useParams } from "react-router-dom";
import {
  deleteOrganizationUser,
  getOrganizationUser,
} from "@/api/endpoints/organization";
import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useState } from "react";
import { ROUTES } from "@/routes_new";
import ErrorComponent from "@components/ui/error";
import { Skeleton } from "@components/ui/skeleton";

export default function DeleteUser() {
  const { t } = useLingui();

  // Local state
  const [isOpen, setIsOpen] = useState<boolean>(true);

  // URL state
  const history = useHistory();

  const { orgUuid, userId } = useParams<{
    orgUuid: string;
    userId: string;
  }>();

  // Handlers
  function handleOpenChange(open: boolean) {
    if (!open) {
      setIsOpen(false);
      setTimeout(() => {
        history.push(generatePath(ROUTES.SETTINGS_TEAM_ORGID, { orgUuid }));
      }, 300);
    }
  }

  // Server state
  const queryClient = useQueryClient();

  const {
    data: userData,
    error: userError,
    isLoading: isUserLoading,
  } = useQuery({
    queryKey: [orgUuid, "users", userId],
    queryFn: () => {
      return getOrganizationUser({
        organization_uuid: orgUuid,
        user_id: Number(userId),
      });
    },
  });

  const { mutate: mutateDeleteUser, isPending: isDeletingUser } = useMutation({
    mutationFn: () =>
      deleteOrganizationUser({
        organization_uuid: orgUuid,
        user_id: parseInt(userId),
      }),
    onSuccess: (res) => {
      if (!res.ok) {
        throw new Error();
      }

      postTrackingData(EVENT.REMOVE_USER, {
        email: userData?.ok ? userData.data.email : undefined,
      });

      toast.success(t`Användare raderad`);
      queryClient.invalidateQueries({
        queryKey: [orgUuid, "users"],
      });
      handleOpenChange(false);
    },
    onError: () => {
      toast.error(t`Misslyckades att ta bort användare`);
    },
  });

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild></AlertDialogTrigger>
      <AlertDialogContent>
        {userData ? (
          userData.ok ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  <Trans>Ta bort</Trans>{" "}
                  {userData.data.name || userData.data.email}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  <Trans>
                    Är du säker på att du vill ta bort den här användaren? Den
                    här åtgärden kan inte ångras.
                  </Trans>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeletingUser}>
                  <Trans>Avbryt</Trans>
                </AlertDialogCancel>
                <AlertDialogAction
                  variant="destructive"
                  disabled={isDeletingUser}
                  onClick={() => mutateDeleteUser()}
                >
                  <Trans>Ta bort</Trans>
                  {isDeletingUser && <Spinner />}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <ErrorComponent
              heading={t`Åh nej!`}
              description={t`Någonting gick fel när vi försökte hämta den här användaren. Vänligen försök igen senare!`}
            />
          )
        ) : isUserLoading ? (
          <>
            <Skeleton className="h-7 w-1/2" />
            <Skeleton className="h-10" />
            <AlertDialogFooter>
              <Skeleton className="h-9 w-20" />
              <Skeleton className="h-9 w-20" />
            </AlertDialogFooter>
          </>
        ) : userError ? (
          <ErrorComponent
            heading={t`Åh nej!`}
            description={t`Någonting gick fel när vi försökte hämta den här användaren. Vänligen försök igen senare!`}
            error={userError}
          />
        ) : null}
      </AlertDialogContent>
    </AlertDialog>
  );
}
